import {
  RhCard,
  RhChip,
  RhSkeletonRectangle,
  RhTimeline,
  RhTimelineItem,
} from "@rhythm-ui/react";
import React, { useState } from "react";

import {
  JobRequirementProgressType,
  JobRequirementSubmissionStatus,
} from "@/types/utils";

const JobStatusProgress = ({
  jobRequirementStatus,
  isJobRequirementStatusLoading,
  className,
}: {
  jobRequirementStatus: JobRequirementProgressType | undefined;
  isJobRequirementStatusLoading: boolean;
  className?: string;
}) => {
  if (isJobRequirementStatusLoading) {
    return (
      <RhCard
        className={`mb-tsm p-tsm flex justify-between overflow-visible ${
          className ?? ""
        }`}
      >
        <RhSkeletonRectangle className="!h-[20px]" />
        <div className="border-l border mx-tm2 " />
        <RhSkeletonRectangle className="flex-1 ml-tmini !h-[20px]" />
      </RhCard>
    );
  }

  return (
    <RhCard
      className={`my-tsm p-tsm flex gap-tnano overflow-visible ${
        className ?? ""
      }`}
    >
      <div
        className="flex items-center gap-tnano"
        data-testid="applicants-count"
      >
        <StatusIndicator
          statusName={"Applicants"}
          statusCount={jobRequirementStatus?.Total.toString() ?? "-"}
        />
        <div className="border-l h-full border mx-tm2 " />
      </div>
      <div className="flex justify-between flex-1">
        {jobRequirementStatus?.submission_status.map((item) => (
          <div className="flex gap-tnano" key={item.id}>
            <StatusIndicator
              statusName={item.status}
              statusCount={item?.candidate_count?.toString() ?? "-"}
            />
            {item.status === "In Interviews" && <ViewStages item={item} />}
          </div>
        ))}
      </div>
    </RhCard>
  );
};

export default JobStatusProgress;

const StatusIndicator = ({
  statusName,
  statusCount,
}: {
  statusName: string;
  statusCount: string;
}) => {
  const statusColor: Record<string, string> = {
    Applicants: "bg-gray-300",
    "To Review": "bg-warning-600",
    "In Interviews": "bg-primary-500",
    Offered: "bg-purple-500",
    Joined: "bg-success-500",
    Rejected: "bg-danger-500",
  };

  return (
    <div className="flex gap-tnano items-center">
      <div
        className={`h-[8px] w-[8px] rounded-full ${statusColor[statusName]}`}
      ></div>
      <div className="flex gap-tpico">
        <p>{statusCount}</p>
        <p className="text-hint">{statusName}</p>
      </div>
    </div>
  );
};

const ViewStages = ({ item }: { item: JobRequirementSubmissionStatus }) => {
  const [isViewStagesOpen, setIsViewStagesOpen] = useState(false);
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <div
        data-testid="interview-stages-chip"
        onMouseEnter={() => setIsViewStagesOpen(true)}
        onMouseLeave={() => setIsViewStagesOpen(false)}
      >
        <RhChip
          className={
            isViewStagesOpen
              ? "chip-gray chip-small"
              : "chip-small text-gray-600"
          }
        >
          View All Stages
        </RhChip>
      </div>

      {isViewStagesOpen && item?.stages && item?.stages.length !== 0 && (
        <div
          className="absolute z-50 rounded-tpico left-[-70px]"
          onMouseEnter={() => setIsViewStagesOpen(true)}
          onMouseLeave={() => setIsViewStagesOpen(false)}
        >
          <div className="z-50 m-auto w-0 h-0 border-l-[8px] border-l-transparent border-b-[8px] border-b-white border-r-[8px] border-r-transparent"></div>

          <div className="bg-white shadow-[0_4px_8px_0px_rgba(0,0,0,0.24)]  min-w-[250px] py-tmini flex flex-col gap-tpico rounded-sm">
            <p className="py-tpico px-tsm text-hint">
              Candidate in Interview Stages
            </p>

            <RhTimeline
              className="mx-tsm !my-0 relative"
              timelineCircleClass="border-none !bg-primary-500 h-tnano w-tnano top-tnano mx-[4.6px] mt-[-5px]"
              timelineLineClass="w-[1px] !bg-gray-100 top-[17px] h-[calc(80%)] mx-tpico"
              align="right"
            >
              {item?.stages.map((stage: any, idx: number) => (
                <RhTimelineItem key={idx} className="-ml-tpico">
                  <p>
                    {stage.candidate_count}
                    <span className="text-hint" data-testid="stages-text">
                      {" "}
                      In {stage.status}
                    </span>
                  </p>
                </RhTimelineItem>
              ))}
            </RhTimeline>
          </div>
        </div>
      )}
    </div>
  );
};
